import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { NationBadge } from "@/components/Compliance";
import { toast } from "sonner";
import { Download, Trash2, Mail } from "lucide-react";
import ReminderSettings from "@/components/ReminderSettings";
import Contractors from "@/components/Contractors";
import AutoBooking from "@/components/AutoBooking";
import TeamPanel from "@/components/TeamPanel";
import Billing from "@/components/Billing";

export default function Settings() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const exportData = async () => {
    const { data } = await api.get("/account/export");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "certy-data-export.json"; a.click();
    toast.success("Your data has been exported");
  };

  const deleteAccount = async () => {
    if (!window.confirm("This permanently deletes your account and all records. Continue?")) return;
    await api.delete("/account");
    await logout();
    nav("/");
  };

  return (
    <Layout>
      <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-6">Settings</h1>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-card border border-border rounded-2xl p-6">
          <h2 className="font-heading font-bold mb-3">Your account</h2>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-muted-foreground">Name</dt><dd className="font-medium">{user?.name || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Email</dt><dd className="font-medium">{user?.email}</dd></div>
            <div className="flex justify-between items-center"><dt className="text-muted-foreground">Nations</dt><dd className="flex gap-1">{user?.nations?.map((n) => <NationBadge key={n} nation={n} />)}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Plan</dt><dd className="font-medium capitalize">{user?.plan}</dd></div>
          </dl>
          <div className="mt-4 flex items-center gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-xs text-emerald-900">
            <Mail size={15} /> <span className="font-mono">{user?.forwarding_email}</span>
          </div>
        </div>

        <Billing />

        <ReminderSettings />
        <Contractors />
        <TeamPanel />
        <AutoBooking />

        <div className="bg-card border border-border rounded-2xl p-6 lg:col-span-2">
          <h2 className="font-heading font-bold mb-1">Data & privacy (UK GDPR)</h2>
          <p className="text-sm text-muted-foreground mb-4">Export or permanently delete all your data at any time.</p>
          <div className="flex flex-wrap gap-3">
            <button data-testid="export-data" onClick={exportData} className="min-h-[44px] px-4 inline-flex items-center gap-2 rounded-xl border border-border font-medium hover:bg-secondary transition-colors"><Download size={16} /> Export my data</button>
            <button data-testid="delete-account" onClick={deleteAccount} className="min-h-[44px] px-4 inline-flex items-center gap-2 rounded-xl border border-rose-300 text-rose-600 font-medium hover:bg-rose-50 transition-colors"><Trash2 size={16} /> Delete my account</button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
